import { getWakeWordDetectionService, WakeWordDetectionService, DetectionStrategy, VADState } from './wake-word-detection-service'
import { getTextToSpeechService, TextToSpeechService } from './text-to-speech-service'
import { TrainingCaptureService, type ConsentGrants } from '@/lib/training/training-capture-service'
import { identifyCurrentSpeaker } from '@/lib/training/identify-speaker'
import { getTrainingCaptureCoordinator } from '@/lib/training/training-capture-coordinator'
import { createClient } from '@/lib/supabase/client'

// Bumped in lock-step with services/training/consent_gate.py + the
// circle_members.consent_version row. Don't change here in isolation.
const CURRENT_CONSENT_VERSION = 'v1-2026-05-16'

export enum LiveSessionState {
  IDLE = 'idle',
  LISTENING = 'listening',
  THINKING = 'thinking',
  SPEAKING = 'speaking',
  ERROR = 'error'
}

export interface LiveSessionConfig {
  voiceId: string
  speechRate: number
  systemPrompt?: string
}

export class LinkLiveSession {
  private static instance: LinkLiveSession
  private state: LiveSessionState = LiveSessionState.IDLE
  private config: LiveSessionConfig = {
    voiceId: 'alloy',
    speechRate: 1.0
  }

  private wakeWordService: WakeWordDetectionService
  private ttsService: TextToSpeechService

  // Training-data capture (Stage 10/13 + personal-AI). Lazy-initialized
  // on first startSession() so it can resolve the user's household +
  // primary circle_member from Supabase. Returns null if the user has
  // not signed training consent — capture is then a no-op.
  private trainingCapture: TrainingCaptureService | null = null
  private trainingConsent: ConsentGrants | null = null
  private trainingHouseholdId: string | null = null

  private listeners: Set<(state: LiveSessionState) => void> = new Set()
  private transcriptListeners: Set<(text: string, isUser: boolean) => void> = new Set()

  // Timer IDs for proper cleanup
  private vadPollingInterval: ReturnType<typeof setInterval> | null = null
  private speakingCheckInterval: ReturnType<typeof setInterval> | null = null

  private constructor() {
    this.wakeWordService = getWakeWordDetectionService()
    this.ttsService = getTextToSpeechService()

    // Subscribe to wake word detection
    this.wakeWordService.subscribe('wakeWordDetected', () => {
      if (this.state === LiveSessionState.IDLE || this.state === LiveSessionState.SPEAKING) {
        this.handleWakeWord()
      }
    })

    // Monitor VAD for interruptions
    // polling or event based? WakeWordService has processing loop.
    // Ideally WakeWordService would emit VAD events.
    // For now we rely on the internal loop of WakeWordService to handle basic listening.
    // But for interruption, we need to know when user STARTS speaking.

    // We can poll VAD state or add a listener if WakeWordService supports it.
    // Looking at the code, it notifies listeners on 'wakeWordDetected'.
    // It implies we might need to modify WakeWordService to emit 'vadStateChanged'.
    // I'll assume for this implementation we can get VAD state updates or just rely on 'wakeWordDetected' for now for simplicity, 
    // but a true "LinkBox Voice" needs interruption on ANY speech. 

    // Let's rely on the fact that if we are in Live Mode, the microphone is ON.
    // I'll assume we can listen to the VAD state via polling in this session manager if active.
    this.vadPollingInterval = setInterval(() => {
      if (this.state === LiveSessionState.SPEAKING) {
        const vadState = this.wakeWordService.getVADState()
        if (vadState === VADState.SPEECH_START || vadState === VADState.SPEECH) {
          this.interrupt()
        }
      }
    }, 100)
  }

  static getInstance(): LinkLiveSession {
    if (!LinkLiveSession.instance) {
      LinkLiveSession.instance = new LinkLiveSession()
    }
    return LinkLiveSession.instance
  }

  public getConfig(): LiveSessionConfig {
    return { ...this.config }
  }

  public updateConfig(config: Partial<LiveSessionConfig>) {
    this.config = { ...this.config, ...config }
    this.ttsService.setSpeechRate(this.config.speechRate)
    // ttsService.setVoice(config.voiceId) // Need to map our IDs to system voices
  }

  public async startSession() {
    console.log('[LinkLive] Starting session...')
    await this.wakeWordService.initialize()
    await this.wakeWordService.startListening()

    // Set strategy to Hybrid for best performance/accuracy
    this.wakeWordService.setStrategy(DetectionStrategy.HYBRID)

    // Initialize training-data capture (no-op if user hasn't consented).
    // Done in parallel with wake-word setup; failures here never block
    // the voice agent path.
    this.initTrainingCapture().catch((e) =>
      console.warn('[LinkLive] training capture init failed:', e),
    )

    // Wardrobe-platform SDK capture pathway (lbx-training.ts). Parallel
    // to the in-house TrainingCaptureService above; coordinator is
    // identity-gated OFF by default until household_id / member_id are
    // wired, so this is a silent no-op in normal flows. Mirrors the iOS
    // (LbxTrainingCoordinator.beginVoiceSession) + Android
    // (LbxTrainingCaptureService.onVoiceSessionStart) pattern.
    getTrainingCaptureCoordinator()
      .beginVoiceSession({ agentId: 'linklive', context: 'voice_query' })
      .catch((e) => console.warn('[LinkLive] lbx-training coordinator begin failed:', e))

    this.setState(LiveSessionState.IDLE)
  }

  /**
   * Resolve the current user's household + "self" circle_member from
   * Supabase and prime a TrainingCaptureService. If anything's missing
   * (no household, no consent, no auth), leaves trainingCapture = null
   * and capture is silently skipped for the session.
   */
  private async initTrainingCapture(): Promise<void> {
    const supabase = createClient()
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return

    const { data: hh } = await supabase
      .from('households').select('id')
      .eq('primary_user_id', user.id).limit(1).maybeSingle()
    if (!hh) return

    const { data: cm } = await supabase
      .from('circle_members')
      .select('id, consent_audio, consent_camera, consent_training, consent_version, bipa_ack')
      .eq('household_id', hh.id).eq('relationship', 'self')
      .limit(1).maybeSingle()
    if (!cm || !cm.consent_training) return

    this.trainingConsent = {
      audio: cm.consent_audio,
      camera: cm.consent_camera,
      training: cm.consent_training,
      version: cm.consent_version,
      bipaAck: cm.bipa_ack,
    }
    this.trainingHouseholdId = hh.id
    this.trainingCapture = new TrainingCaptureService({
      supabase, householdId: hh.id, memberId: cm.id,
      getConsent: () => this.trainingConsent!,
    })
    try {
      await this.trainingCapture.startSession({
        agentId: 'linklive',
        context: 'voice_query',
        lang: navigator.language || 'en-US',
      })
    } catch (e) {
      console.warn('[LinkLive] trainingCapture.startSession failed:', e)
      this.trainingCapture = null
    }
  }

  public async stopSession() {
    console.log('[LinkLive] Stopping session...')
    this.ttsService.stop()
    await this.wakeWordService.stopListening()

    // Flush + close the training-capture session if one's running.
    if (this.trainingCapture) {
      try {
        await this.trainingCapture.stopCapture()
        await this.trainingCapture.endSession()
      } catch (e) {
        console.warn('[LinkLive] trainingCapture stop failed:', e)
      }
      this.trainingCapture = null
      this.trainingConsent = null
    }

    // Close out the wardrobe SDK capture session in parallel. Safe to
    // call when no session is active (the coordinator no-ops).
    try {
      await getTrainingCaptureCoordinator().endVoiceSession()
    } catch (e) {
      console.warn('[LinkLive] lbx-training coordinator end failed:', e)
    }

    // Clean up all polling intervals
    if (this.vadPollingInterval) {
      clearInterval(this.vadPollingInterval)
      this.vadPollingInterval = null
    }
    if (this.speakingCheckInterval) {
      clearInterval(this.speakingCheckInterval)
      this.speakingCheckInterval = null
    }

    this.setState(LiveSessionState.IDLE)
  }

  private handleWakeWord() {
    console.log('[LinkLive] Wake word detected!')
    this.interrupt() // Stop speaking if we were
    this.setState(LiveSessionState.LISTENING)

    // Begin training capture for this interaction. No-op if user
    // hasn't consented. Chunks flush every 30s; the next stopSession()
    // closes out the trailing partial chunk.
    //
    // BEFORE startCapture: sample ~3.5s of audio and ask the
    // personalization API who's actually talking. If we get a strong
    // match against an enrolled member, retag the capture so chunks
    // upload with the right member_id from the first one. Fallback to
    // the household primary "self" if no enrollment matches — the
    // server-side diarize sweep will catch any misattribution later.
    if (this.trainingCapture && this.trainingHouseholdId) {
      const cap = this.trainingCapture
      const householdId = this.trainingHouseholdId
      identifyCurrentSpeaker({ householdId })
        .then((id) => {
          if (!id.unknown) {
            console.log(`🏎️ [LinkLive] live-id: ${id.displayName} (${id.confidence.toFixed(2)})`)
            cap.setMember(id.memberId)
          } else {
            console.log(`🏎️ [LinkLive] live-id: unknown (${id.reason}) — using session default`)
          }
        })
        .catch((e) => console.warn('[LinkLive] identify-speaker failed:', e))
        .finally(() => {
          cap.startCapture().catch((e) =>
            console.warn('[LinkLive] trainingCapture.startCapture failed:', e),
          )
        })
    }
    // Play a listening sound?

    // In a real implementation, we would now stream audio to the LLM or STT service.
    // For this client-side prototype, we rely on the SpeechRecognition that WakeWordService uses.
    // We need to hook into the transcript results.
    // The WakeWordService doesn't expose raw transcripts easily in the file I read. 
    // I might need to use my own SpeechRecognition instance or modify WakeWordService.
    // For safety, I'll simulate the "Listening" phase completion with a mock for now
    // OR try to access the speech recognition from WakeWordService if exposed.

    // Actually, looking at WakeWordDetectionService, it seems to handle "wake words" but not general transcription for the full query.
    // I will simulate the flow:
    // 1. Wake word detected.
    // 2. Start capturing User Query (using browser SpeechRecognition directly here for simplicity).
    this.captureUserQuery()
  }

  private captureUserQuery() {
    // This is a simplified client-side capture. 
    // Real LinkBox Voice would stream audio bytes.
    if (!('webkitSpeechRecognition' in window)) {
      console.error("Speech recognition not supported")
      return
    }

    const recognition = new (window as any).webkitSpeechRecognition()
    recognition.continuous = false
    recognition.interimResults = false
    recognition.lang = 'en-US'

    recognition.onresult = (event: any) => {
      const text = event.results[0][0].transcript
      console.log(`[LinkLive] User said: ${text}`)
      this.notifyTranscript(text, true)
      this.processQuery(text)
    }

    recognition.onerror = (event: any) => {
      console.error("Recognition error", event.error)
      this.setState(LiveSessionState.IDLE)
    }

    recognition.start()
  }

  private async processQuery(text: string) {
    this.setState(LiveSessionState.THINKING)

    try {
      // Call the unified AI chat endpoint
      const response = await fetch('/api/ai/unified/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: text,
          context: {
            source: 'voice',
            systemPrompt: this.config.systemPrompt
          }
        }),
      })

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`)
      }

      const data = await response.json()
      const responseText = data.response || data.message || data.content || 'I understood your request.'

      this.notifyTranscript(responseText, false)
      this.speakResponse(responseText)
    } catch (error) {
      console.error('[LinkLive] AI processing error:', error)

      // Fallback to mock response in development
      if (process.env.NODE_ENV === 'development') {
        console.warn('[LinkLive] Using fallback response in development mode')
        const fallbackResponses = [
          "I can certainly help with that.",
          "Let me look into that for you.",
          "I've processed your request.",
        ]
        const fallbackText = fallbackResponses[Math.floor(Math.random() * fallbackResponses.length)]
        this.notifyTranscript(fallbackText + ' (Development mode)', false)
        this.speakResponse(fallbackText)
      } else {
        this.notifyTranscript("I'm sorry, I couldn't process that request. Please try again.", false)
        this.speakResponse("I'm sorry, I couldn't process that request. Please try again.")
      }
    }
  }

  private speakResponse(text: string) {
    this.setState(LiveSessionState.SPEAKING)
    this.ttsService.speak(text, {
      speechRate: this.config.speechRate,
      // voiceName: this.config.voiceId // Map this
    })

    // We need to know when speaking finishes to go back to IDLE
    // The TTS service emits events. I should subscribe to them.
    // For this prototype, I'll rely on the 'isSpeaking' check or just assume completion.
    // Better:
    // Clear any previous speaking check interval
    if (this.speakingCheckInterval) {
      clearInterval(this.speakingCheckInterval)
    }
    this.speakingCheckInterval = setInterval(() => {
      if (!this.ttsService.isSpeaking() && this.state === LiveSessionState.SPEAKING) {
        this.setState(LiveSessionState.IDLE)
        if (this.speakingCheckInterval) {
          clearInterval(this.speakingCheckInterval)
          this.speakingCheckInterval = null
        }
      }
    }, 500)
  }

  private interrupt() {
    if (this.state === LiveSessionState.SPEAKING) {
      console.log('[LinkLive] Interrupted!')
      this.ttsService.stop()
      // Clear speaking check timer to prevent orphaned interval
      if (this.speakingCheckInterval) {
        clearInterval(this.speakingCheckInterval)
        this.speakingCheckInterval = null
      }
      this.setState(LiveSessionState.IDLE)
    }
  }

  private setState(state: LiveSessionState) {
    if (this.state !== state) {
      this.state = state
      this.listeners.forEach(cb => cb(state))
    }
  }

  public subscribe(callback: (state: LiveSessionState) => void): () => void {
    this.listeners.add(callback)
    return () => this.listeners.delete(callback)
  }

  public onTranscript(callback: (text: string, isUser: boolean) => void): () => void {
    this.transcriptListeners.add(callback)
    return () => this.transcriptListeners.delete(callback)
  }

  private notifyTranscript(text: string, isUser: boolean) {
    this.transcriptListeners.forEach(cb => cb(text, isUser))
  }
}

export function getLinkLiveSession(): LinkLiveSession {
  return LinkLiveSession.getInstance()
}
